import {
  CLEAN_DATABASE,
  GET_MD,
  GET_REPORT,
  GET_REPORT_STATUS,
  MAIN,
  REFRESH_USERS,
  UPDATE_MD,
  UPDATE_PASSWORD,
} from 'constants/endpoints';
import {
  LAST_DATABASE_CLEANUP,
  LAST_PASSWORD_UPDATE,
  LAST_REPORT_GENERATION,
  LAST_USERS_UPDATE,
} from 'constants/fields';
import { AUTH_ERROR_MSG } from 'constants/messages';
import { createData, downloadFile, getData, modifyData } from 'services';
import { getQuery } from 'store/actions';
import {
  CANCEL_ASSIGN_ENTRY,
  CANCEL_NEW_ENTRY,
  GET_DATA,
  GET_SECTIONS,
  LOGOUT,
  SET_APP_METADATA,
  SET_LOADING,
  SET_MESSAGES,
  SET_RESPONSE_MESSAGES,
  SET_SECTION,
  TOGGLE_MOBILE,
  UNSET_LOADING,
  UNSET_MESSAGES,
} from 'store/actions/types';
import { genericAction } from 'store/actions/utils';

const getSections = () => {
  return getQuery(MAIN, GET_SECTIONS);
};

const setDefaultSection = () => {
  return async (dispatch) => {
    dispatch(genericAction(SET_SECTION, { name: '', endpoint: '' }));
  };
};

const setSection = (section) => {
  return async (dispatch) => {
    dispatch(genericAction(SET_SECTION, section));
  };
};

const unsetLoading = () => {
  return async (dispatch) => {
    dispatch(genericAction(UNSET_LOADING));
  };
};

const cancelNewEntry = () => {
  return async (dispatch) => {
    dispatch(genericAction(CANCEL_NEW_ENTRY));
  };
};

const cancelAssignEntry = () => {
  return async (dispatch) => {
    dispatch(genericAction(CANCEL_ASSIGN_ENTRY));
  };
};

const setMessages = () => {
  return async (dispatch) => {
    dispatch(genericAction(SET_MESSAGES));
  };
};

const unsetMessages = () => {
  return async (dispatch) => {
    dispatch(genericAction(UNSET_MESSAGES));
  };
};

const toggleMobile = () => {
  return async (dispatch) => {
    dispatch(genericAction(TOGGLE_MOBILE));
  };
};

const refreshMetadata = (token = null) => {
  return async (dispatch) => {
    dispatch(genericAction(SET_LOADING));
    try {
      const body = await getData(GET_MD, token);
      dispatch(genericAction(SET_APP_METADATA, body));
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    } finally {
      dispatch(genericAction(UNSET_LOADING));
    }
  };
};

const updateMetadata = (payload, token = null) => {
  return async (dispatch) => {
    dispatch(genericAction(SET_LOADING));
    try {
      await createData(UPDATE_MD, payload, token);
      dispatch(refreshMetadata(token));
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    } finally {
      dispatch(genericAction(UNSET_LOADING));
    }
  };
};

const updateAdminPassword = (payload, token = null) => {
  return async (dispatch) => {
    dispatch(genericAction(SET_LOADING));
    try {
      await modifyData(UPDATE_PASSWORD, payload, token);
      await createData(UPDATE_MD, { [LAST_PASSWORD_UPDATE]: null }, token);
      dispatch(refreshMetadata(token));
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    } finally {
      dispatch(genericAction(UNSET_LOADING));
    }
  };
};

const getReport = (year, token = null) => {
  return async (dispatch) => {
    dispatch(genericAction(SET_LOADING));
    try {
      const path = `${GET_REPORT}/${year}`;
      const filename = `${year}_annual_report.zip`;
      await downloadFile(path, filename, token);
      await createData(UPDATE_MD, { [LAST_REPORT_GENERATION]: null }, token);
      dispatch(refreshMetadata(token));
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    } finally {
      dispatch(genericAction(UNSET_LOADING));
    }
  };
};

const getReportStatus = (year, token = null) => {
  return async (dispatch) => {
    try {
      dispatch(genericAction(GET_DATA, { data: null }));
      const path = `${GET_REPORT_STATUS}/${year}`;
      const body = await getData(path, token);
      dispatch(genericAction(GET_DATA, { data: body }));
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    }
  };
};

const refreshUsers = (payload, token = null, provisional = 'true') => {
  return async (dispatch) => {
    try {
      const body = await createData(REFRESH_USERS, payload, token, true, {
        provisional: provisional,
      });
      await createData(UPDATE_MD, { [LAST_USERS_UPDATE]: null }, token);
      if (provisional === 'true') {
        dispatch(genericAction(SET_RESPONSE_MESSAGES, { data: body }));
      }
      if (provisional === 'false') {
        dispatch(clearResponseMessages());
        dispatch(refreshMetadata(token));
      }
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    }
  };
};

const clearResponseMessages = () => {
  return async (dispatch) => {
    dispatch(genericAction(SET_RESPONSE_MESSAGES, { data: [''] }));
  };
};

const cleanDatabase = (token) => {
  return async (dispatch) => {
    dispatch(genericAction(SET_LOADING));
    try {
      await getData(CLEAN_DATABASE, token);
      await createData(UPDATE_MD, { [LAST_DATABASE_CLEANUP]: null }, token);
      dispatch(refreshMetadata(token));
    } catch (error) {
      if (AUTH_ERROR_MSG.includes(error.message)) {
        dispatch(genericAction(LOGOUT));
      }
    } finally {
      dispatch(genericAction(UNSET_LOADING));
    }
  };
};

export {
  cancelAssignEntry,
  cancelNewEntry,
  cleanDatabase,
  clearResponseMessages,
  getReport,
  getReportStatus,
  getSections,
  refreshMetadata,
  refreshUsers,
  setDefaultSection,
  setMessages,
  setSection,
  toggleMobile,
  unsetLoading,
  unsetMessages,
  updateAdminPassword,
  updateMetadata,
};
